import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { PropTypes } from "prop-types";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AutoCompleteMenu, Calendar, FileLink, Loader, UploadInput } from "@/atoms";
import { FormContainer } from "@/components";
import {
    actionsData,
    fileData,
    identifiersData,
    inputData,
    permissionsData,
    releaseInfoFormData,
    selectionsData,
} from "@/data";
import {
    useArtistsList,
    useGenresSelectionList,
    useReleaseTypesSelectionList,
    useRightTypesSelectionList,
    useUploadFile,
} from "@/hooks";
import { useReleaseInfoSchema } from "@/schemas";
import { StyledTextField } from "@/styles";
import { canDo, deleteFileHandler, renderFormControllerHandler, setUploadInputPreviousValueHandler } from "@/utils";

const ReleaseInfoForm = ({ action, loading, onSubmitForm, values }) => {
    const { t } = useTranslation();

    const schema = useReleaseInfoSchema();

    const selections = useSelector((state) => state.selectionsReducer);

    const { user } = useAuth0();

    const formMethods = useForm({
        defaultValues: values,
        mode: "onChange",
        resolver: yupResolver(schema),
    });

    const { filesData, filesLoading, setFilesData, uploadFile } = useUploadFile();

    const { loading: releaseTypesSelectionListLoading } = useReleaseTypesSelectionList();

    const { loading: rightTypesSelectionListLoading } = useRightTypesSelectionList();

    const { loading: genresSelectionListLoading } = useGenresSelectionList();

    const {
        count: artistsCount,
        fetched: artistsFetched,
        loading: artistsLoading,
        page: artistsPage,
        refetch: artistsRefetch,
        searchKey: artistsSearchKey,
    } = useArtistsList(true, true, {}, null);

    const { mediaCreate: mediaCreatePermission, mediaEdit: mediaEditPermission } = permissionsData.permissions.keys;

    const allowedPermissions = action === actionsData.create ? [mediaCreatePermission] : [mediaEditPermission];

    const {
        control,
        formState: { errors },
        setError,
        setValue,
        watch,
    } = formMethods;

    const {
        checkBox: checkBoxInputType,
        date: dateInputType,
        file: fileInputType,
        text: textInputType,
    } = inputData.types;

    const asyncMenus = {
        artists: {
            count: artistsCount,
            loading: artistsLoading,
            page: artistsPage,
            refetch: artistsRefetch,
            searchKey: artistsSearchKey,
            selectionKey: selectionsData.keys.artists,
        },
    };

    const renderFormControllerChildrenHandler = (field, label, name, type, isOptional, selectionKey, isAsyncMenu) => {
        switch (type) {
            case textInputType:
                return (
                    <StyledTextField
                        {...field}
                        error={errors[name]}
                        helperText={errors[name] && errors[name]?.message}
                        id={`${name}_${identifiersData.ids.input}`}
                        label={isOptional ? `${t(`labels.${label}`)} (${t("labels.optional")})` : t(`labels.${label}`)}
                        value={field?.value}
                        variant="outlined"
                        fullWidth
                    />
                );
            case checkBoxInputType:
                return (
                    <FormControlLabel
                        control={<Checkbox id={`${name}_${identifiersData.ids.input}`} />}
                        {...field}
                        checked={field.value}
                        error={errors[name]}
                        label={t(`labels.${label}`)}
                        variant="outlined"
                    />
                );
            case dateInputType:
                return (
                    <Calendar
                        errors={errors}
                        field={field}
                        isOptional={isOptional}
                        label={label}
                        name={name}
                        value={field.value || null}
                    />
                );
            case fileInputType:
                return (
                    <Box
                        sx={{
                            mb: 1,
                        }}
                    >
                        <UploadInput
                            accept={fileData.acceptedExtensions.image}
                            color={errors[name]?.message ? "error" : "primary"}
                            error={errors[name]?.message}
                            field={field}
                            hasError={!!errors[name]?.message}
                            label={t(`labels.${label}`)}
                            loading={filesLoading?.[name]}
                            onChange={(e) => {
                                uploadFile(e.target.files[0], formMethods, name);
                            }}
                        />
                        {values?.oldImage && !filesData?.[name] && (
                            <FileLink
                                link={values?.oldImage?.downloadThumbnail}
                                name={values?.oldImage?.downloadThumbnail}
                                hasDownload
                                hasImage
                                hasJustifyStart
                            />
                        )}
                        {filesData?.[name] && !filesLoading?.[name] && (
                            <FileLink
                                name={filesData?.[name]?.name}
                                deleteAction={() => deleteFileHandler(setFilesData, formMethods, name)}
                                hasDelete
                            />
                        )}
                    </Box>
                );
            default:
                return (
                    <AutoCompleteMenu
                        asyncMenuData={asyncMenus?.[selectionKey]}
                        defaultValue={
                            values?.[`${name}Obj`] ||
                            selections?.[selectionKey]?.find((option) => option.value === values?.[name])
                        }
                        errors={errors}
                        getRenderedOption={(option) => option.label}
                        isAsyncMenu={isAsyncMenu}
                        isOptional={isOptional}
                        label={label}
                        name={name}
                        optionLabel={(option) => option.label}
                        options={selections?.[selectionKey]}
                        onChange={(_, option) => {
                            if (option) field.onChange(option.value);
                            else field.onChange("");
                        }}
                    />
                );
        }
    };

    useEffect(
        () => {
            setUploadInputPreviousValueHandler(watch, setError, setValue, filesData, fileData.names.image);
        },
        [watch(fileData.names.image)], // eslint-disable-line
    );

    if (
        (artistsLoading && !artistsFetched) ||
        releaseTypesSelectionListLoading ||
        rightTypesSelectionListLoading ||
        genresSelectionListLoading ||
        !selections.hasLoaded.artists
    )
        return <Loader />;

    return (
        <FormContainer
            action={action}
            disabled={filesLoading?.imageFile}
            formMethods={formMethods}
            loading={loading}
            data={renderFormControllerHandler(control, releaseInfoFormData, renderFormControllerChildrenHandler)}
            hasSubmitButton={canDo(user, allowedPermissions)}
            onSubmitForm={onSubmitForm}
        />
    );
};

export default ReleaseInfoForm;

ReleaseInfoForm.propTypes = {
    action: PropTypes.string,
    loading: PropTypes.bool,
    onSubmitForm: PropTypes.func,
    values: PropTypes.object,
};
